<template>
  <div>
    <template v-if="order">
      <div class="d-flex mb-3 align-items-end">
        <h1 class="h4 primary--text mr-auto">
          {{ $t('navigation.order') }}
          <span v-if="order.number">
            {{ order.number }}
          </span>
        </h1>
        <div class="ml-auto d-flex">
          <router-link
            to="/orders"
            class="shipment-notice__back-link my-auto"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="10"
              height="10"
              viewBox="0 0 10 10"
            >
              <path
                d="M9,4l.881.881L6.394,8.375H14v1.25H6.394l3.487,3.494L9,14,4,9Z"
                transform="translate(-4 -4)"
              />
            </svg>
            {{ $t('views.order_list.back_to_list') }}
          </router-link>
        </div>
      </div>
      <v-card elevation="0">
        <div class="d-flex p-4 border-bottom">
          <div
            v-if="order.dealerInternalOrder"
            class="mr-4"
          >
            <span class="mr-2 grey--text">{{ $t('common.internal_dealer_order') }}:</span>
            <span>{{ order.dealerInternalOrder }}</span>
          </div>
          <div v-if="order.comments">
            <span class="mr-2 grey--text">{{ $t('common.order_comment') }}:</span>
            <span>{{ order.comments }}</span>
          </div>
          <div
            v-if="order.VIN"
            class="ml-auto"
          >
            <span class="mr-2 grey--text"> VIN:</span>
            <span>{{ order.VIN }}</span>
          </div>
        </div>
        <v-data-table
          v-model="selectedItems"
          class="order-table mb-4"
          fixed-header
          :headers="headers"
          :items="items"
          item-key="code"
          show-select
          :footer-props="{
            itemsPerPageOptions: [10, 20, 50, 100],
          }"
          :loading="loading"
          :loading-text="$t('common.loading_data')"
        >
          <template v-slot:[`item.quantity`]="{ item }">
            <v-text-field
              v-if="orderCreated"
              v-model.number="item.quantity"
              class="w-80"
              hide-details
              dense
              type="number"
              min="1"
              step="1"
            />
            <div v-else>
              {{ item.quantity }}
            </div>
          </template>
          <template
            v-if="orderCreated"
            v-slot:[`item.actions`]="{ item }"
          >
            <v-hover v-slot="{hover}">
              <v-icon
                :class="hover ? '' : 'text--disabled'"
                color="primary"
                size="20"
                @click="removePart(item)"
              >
                mdi-delete
              </v-icon>
            </v-hover>
          </template>
          <template v-slot:item.confirmedQuantity="{ item }">
            {{ item.confirmedQuantity }}
            <v-tooltip
              v-if="item.supplierComment"
              top
            >
              <template v-slot:activator="{ on, attrs }">
                <v-hover v-slot:default="{hover}">
                  <v-icon
                    class="mr-2 pl-1"
                    :class="hover ? '' : 'text--disabled'"
                    color="primary"
                    size="20"
                    v-bind="attrs"
                    v-on="on"
                    @click="showComment(item.supplierComment)"
                  >
                    mdi-comment-outline
                  </v-icon>
                </v-hover>
              </template>
              <span>{{ $t('common.supplier_comment') }}</span>
            </v-tooltip>
          </template>
        </v-data-table>
        <div class="d-flex p-4 border-top">
          <div>
            <span>{{ $t('common.status') }}: </span>
            <span class="h5 primary--text">{{ $t(`enums.order_statuses.${order.status.toLowerCase()}`) }}</span>
          </div>
          <div class="ml-auto mr-4">
            <span class="mr-2 grey--text">{{ $t('common.total_price_without_vat') }}:</span>
            <span>{{ orderDealersTotal | numberFormatter }}</span>
            <!-- <span>{{ order.total }}</span> -->
          </div>
          <div class="mr-4">
            <span class="mr-2 grey--text">{{ $t('common.total_vat') }}:</span>
            <span>{{ order.totalVAT | numberFormatter }}</span>
          </div>
          <div>
            <span class="mr-2 grey--text">{{ $t('common.total_price_with_vat') }}:</span>
            <span>{{ order.totalWithVAT | numberFormatter }}</span>
          </div>
        </div>
      </v-card>
      <div
        v-if="orderCreated"
        class="d-flex mt-6 align-items-baseline"
      >
        <v-btn
          depressed
          outlined
          :disabled="orderApproved"
          :loading="approvalLoading"
          @click="approveOrder"
        >
          {{ orderApproved ? $t('common.order_approved') : $t('common.order_approve') }}
        </v-btn>
        <div />
        <span class="ml-auto mr-4 grey--text"> Тип заказа:</span>
        <v-select
          v-model="order.orderType"
          class="order-item-select"
          hide-details
          outlined
          dense
          :items="orderTypes"
        >
          {{ $t('views.order_list.correction') }}
        </v-select>
        <v-btn
          class="ml-4"
          depressed
          outlined
          :disabled="!orderEditable"
          :loading="editOrderLoading"
          @click="editOrder"
        >
          {{ $t('views.order_list.correction') }}
        </v-btn>
      </div>
      <div class="mt-8 mb-4 d-flex align-items-end">
        <div
          v-if="orderHasDocs"
        >
          <div class="mb-3">
            {{ $t('views.order_list.get_doc') }}
          </div>
          <v-btn
            v-if="order.invoice.filePath"
            :href="order.invoice.filePath"
            class="text-decoration-none mr-4"
            depressed
            outlined
          >
            {{ $t('views.order_list.invoice') }}
          </v-btn>
          <v-btn
            v-if="order.invoiceForPayment.filePath"
            :href="order.invoiceForPayment.filePath"
            class="text-decoration-none mr-4"
            depressed
            outlined
          >
            {{ $t('views.order_list.invoice_for_payment') }}
          </v-btn>
          <v-btn
            v-if="order.packingList.filePath"
            :href="order.packingList.filePath"
            class="text-decoration-none mr-4"
            depressed
            outlined
          >
            {{ $t('views.order_list.print_form_TORG12') }}
          </v-btn>
          <v-btn
            v-if="order.universalTransferDocument.filePath"
            :href="order.universalTransferDocument.filePath"
            class="text-decoration-none mr-4"
            depressed
            outlined
          >
            {{ $t('views.order_list.universal_transfer_document') }}
          </v-btn>
        </div>
        <div
          v-if="orderComplains"
          class="ml-auto"
        >
          <v-btn
            class="text-decoration-none mt-auto"
            depressed
            outlined
            :disabled="!selectedItems.length"
            @click="postComplaintModal = true"
          >
            {{ $t('views.order_list.issue_complaint') }}
          </v-btn>
          <post-complaint-modal
            v-model="postComplaintModal"
            :order-id="order.id"
            :parts-selected="selectedItems"
            @postComplaint="getOrder"
          />
        </div>
      </div>
    </template>
    <b-modal
      v-if="isSupplierCommentShow"
      ref="modal"
      v-model="isSupplierCommentShow"
      :title="$t('common.supplier_comment')"
      modal-class="spdo-modal supplier-comment-modal"
      hide-header-close
      hide-footer
      centered
      size="lg"
    >
      <b-col>
        <div class="shipment-notice__label">
          {{ partSupplierComment }}
        </div>
      </b-col>
      <main class="shipment-notice__main">
        <div class="v-application d-flex mt-4">
          <v-btn
            class="mr-4 flex-grow-1"
            outlined
            large
            @click="isSupplierCommentShow = false"
          >
            {{ $t('common.close') }}
          </v-btn>
        </div>
      </main>
    </b-modal>
  </div>
</template>

<script>
import PostComplaintModal from '@/components/complaints/PostComplaintModal';
import { orderTypes } from '@/utils/enums';
import { mapState } from 'vuex';

export default {
  name: 'Order',
  components: {
    PostComplaintModal,
  },
  props: {
    id: String,
  },
  data() {
    return {
      postComplaintModal: false,
      isSupplierCommentShow: false,
      partSupplierComment: '',
      items: [],
      selectedItems: [],
      loading: false,
      approvalLoading: false,
      editOrderLoading: false,
      order: null,
      orderTypes: orderTypes
        .map((type) => ({ value: type, text: this.$t(`enums.order_types.${type}`) })),
      baseHeaders: [
        {
          text: this.$t('common.article'),
          value: 'code',
        },
        {
          text: this.$t('common.order_name'),
          value: 'name',
        },
        {
          text: this.$t('common.quantity'),
          value: 'quantity',
        },
        {
          text: this.$t('common.complaint_quantity'),
          value: 'complaintQuantity',
        },
        {
          text: this.$t('common.order_confirmed_quantity'),
          value: 'confirmedQuantity',
        },
        {
          text: this.$t('common.delivery_date'),
          value: 'deliveryTime',
        },
        {
          text: this.$t('common.status'),
          value: 'status',
        },
        {
          text: this.$t('common.order_price_rrc'),
          value: 'price',
        },
        {
          text: this.$t('common.VAT_rate'),
          value: 'VATRate',
        },
        {
          text: this.$t('common.dealers_discount'),
          value: 'discount',
        },
        {
          text: this.$t('common.order_dealers_price'),
          value: 'sum',
        },
        {
          text: this.$t('common.order_dealers_price_vat'),
          value: 'sumWithVAT',
        },
      ],
      actionHeader: {
        text: this.$t('common.actions'),
        value: 'actions',
      },
      headers: [],
    };
  },
  computed: {
    ...mapState(['workMode']),

    orderDealersTotal(){
      const total = this.order.parts.reduce((a,v) => a + v.sum,0);
      return parseFloat(total.toFixed(2))
    },

    orderCreated() {
      return this.order.status === 'Created';
    },
    orderApproved() {
      return this.order.status === 'Approved';
    },
    orderEditable() {
      return this.order.status === 'Approved' || this.order.status === 'Created';
    },
    orderComplains() {
      return this.workMode.isModeAurus && (this.order.status === 'Shipped' || this.order.status === 'Closed');
    },
    orderHasDocs() {
      return this.order.invoice.filePath
        || this.order.invoiceForPayment.filePath
        || this.order.packingList.filePath
        || this.order.universalTransferDocument.filePath;
    },
  },
  created() {
    this.setWorkModeLayout();
    this.getOrder();
  },
  methods: {
    showComment(comment) {
      this.partSupplierComment = comment;
      this.isSupplierCommentShow = true;
    },
    setWorkModeLayout() {
      if (this.workMode.isModeSollers) {
        ['complaintQuantity', 'deliveryTime', 'discount']
          .forEach((tblItem) => {
            this.baseHeaders.splice(this.baseHeaders.findIndex((item) => item.value === tblItem), 1);
          });
      }
    },

    async getOrder() {
      this.loading = true;
     
      const { data } = await this.$http.get(`orders/${this.id}`, {
        params: {
          populate: ['invoiceForPayment', 'invoice', 'packingList', 'universalTransferDocument'],
        },
      }).finally(() => {
        this.loading = false;
      });

      this.order = data;
      this.selectedItems = [];

      this.headers = (this.orderCreated) ? [...this.baseHeaders, this.actionHeader] : this.baseHeaders;

      this.items = data.parts.map((it) => ({
        ...it,
        deliveryTime: this.getDate(it.deliveryTime, 'L', 'Europe/Moscow'),
        status: it.status && this.$t(`enums.part_statuses.${it.status}`),
        sumWithVAT:  this.$numberFormatter(it.sumWithVAT),
        sum: this.$numberFormatter(it.sum),
        price: this.$numberFormatter(it.price),
      }));
    },
    async approveOrder() {
      this.approvalLoading = true;

      await this.$http.patch(`orders/${this.id}`, {
        status: 'Approved',
      }).finally(() => {
        this.approvalLoading = false;
      });
      this.order.status = 'Approved';
    },
    async editOrder() {
      this.editOrderLoading = true;
      const parts = this.items.map(({ code, quantity }) => ({ code, quantity }));

      await this.$http.patch(`orders/${this.id}`, {
        orderType: this.order.orderType,
        parts,
      }).finally(() => {
        this.editOrderLoading = false;
      });
    },
    removePart(part) {
      this.items.splice(this.items.indexOf(part), 1);
    },
    postComplaint() {

    },
    getDate(date, format = 'LLL', tz) {
      if (!date) { return ''; }

      const mDate = tz ? this.$moment.tz(date, tz) : this.$moment(date);

      return ((new Date(date)).toISOString() !== '1970-01-01T00:00:00.000Z')
        ? mDate.format(format) : '';
    },
  },
};
</script>

<style lang="scss">
.order-table {
  & th:first-child,
  & td:first-child {
    position: sticky;
    left: 0;
    z-index: 3 !important;
    min-width: 40px;
    background: white;
    // box-shadow: inset -4px 0 5px -2px rgba(0,0,0,.16078);
  }

  & th:first-child {
    box-shadow:
      inset 0 -1px 0 rgba(0, 0, 0, 0.12),
      // inset -4px 0 5px -2px rgba(0, 0, 0, 0.16078) !important;
  }

  & th:last-child,
  & td:last-child {
    position: sticky;
    right: 0;
    z-index: 3 !important;
    min-width: 120px;
    text-align: center !important;
    background: white;
    // box-shadow: inset 4px 0 5px -2px rgba(0,0,0,.16078);
  }

  & th:last-child {
    box-shadow:
      inset 0 -1px 0 rgba(0, 0, 0, 0.12),
      // inset 4px 0 5px -2px rgba(0, 0, 0, 0.16078) !important;

      i {
        display: none;
      }
  }

  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
    background-color: #fff;
  }

  &::-webkit-scrollbar-thumb {
    background-color: var(--amber);
    border-radius: 10px;
  }
}

.order-item-select {
  margin-top: -2px !important;
  max-width: 300px;
}

.w-80 {
  max-width: 80px;
}

</style>
